import { useCallback, useRef, useState } from 'react';

/**
 * Generic persisted boolean flag (#2159). The house localStorage-preference
 * pattern (see `useTileViewMode`, #1893) generalised to a plain on/off flag:
 * a pure UI preference with no server round-trip and no need to travel in a
 * request header.
 *
 * No-flash contract: the persisted value is read in the lazy `useState`
 * initializer, so the very first render already paints the correct state —
 * NOT in a `useEffect` (which would paint the default first, then correct,
 * i.e. the exact flash the AC forbids). Writes mirror the ChatProvider idiom
 * (persist in the setter, not a mount effect). Every access guards `window`
 * (jsdom/SSR) and swallows storage errors (private mode / quota), degrading
 * to the in-memory value.
 */

/**
 * Read a persisted boolean synchronously. Guards `window` and swallows
 * storage errors, degrading to `defaultValue`. Exported so a caller can read
 * the same value in a lazy `useState` initializer without duplicating the
 * guard logic.
 */
export function readPersistedFlag(key: string, defaultValue: boolean): boolean {
    if (typeof window === 'undefined') return defaultValue;
    try {
        const raw = window.localStorage.getItem(key);
        if (raw === 'true') return true;
        if (raw === 'false') return false;
    } catch {
        /* swallow storage errors (private mode, quota) */
    }
    return defaultValue;
}

export interface UsePersistedFlag {
    value: boolean;
    setValue: (next: boolean) => void;
    toggle: () => void;
}

export function usePersistedFlag(key: string, defaultValue: boolean): UsePersistedFlag {
    const [value, setValueState] = useState<boolean>(() => readPersistedFlag(key, defaultValue));

    // Latest value without widening `toggle`'s deps (keeps its identity stable
    // across renders) and without a side effect inside a state updater.
    const valueRef = useRef(value);
    valueRef.current = value;

    const setValue = useCallback(
        (next: boolean) => {
            setValueState(next);
            try {
                window.localStorage.setItem(key, String(next));
            } catch {
                /* swallow storage errors — in-memory state still flips */
            }
        },
        [key],
    );

    const toggle = useCallback(() => {
        setValue(!valueRef.current);
    }, [setValue]);

    return { value, setValue, toggle };
}

export interface UseCollapsiblePersisted {
    /** True = the panel body is collapsed (hidden); default false (expanded). */
    folded: boolean;
    toggle: () => void;
    setFolded: (next: boolean) => void;
}

/**
 * Sugar over `usePersistedFlag` for a fold/unfold panel: names the flag
 * `folded`. Key convention `hofsync.<area>.<pref>`
 * (e.g. `hofsync.deckungsbeitrag.rankChartFolded.v2`).
 *
 * `defaultFolded` defaults to `false` (expanded) — the original contract, kept
 * so every existing caller is byte-identical. Pass `true` for a panel that
 * should open collapsed (#2948 §5). Note the interaction with persistence: a
 * STORED value always wins over the default, so flipping this for a panel that
 * already shipped reaches only users who never touched the control — changing
 * the default for everyone means renaming the key too.
 */
export function useCollapsiblePersisted(
    key: string,
    defaultFolded = false,
): UseCollapsiblePersisted {
    const { value, setValue, toggle } = usePersistedFlag(key, defaultFolded);
    return { folded: value, toggle, setFolded: setValue };
}
